// src/lib/data/strategies/DualSyncStrategy.js - V15 ENTERPRISE ULTRA-ROBUST
// ======================================================
// 🔄 DUAL SYNC STRATEGY - V15 (AIRBNB / ZILLOW / QUINTOANDAR GRADE)
// ======================================================
// Orquestador central blindado — Online/Offline sin errores
//
// 🔥 V15: readWithSync() — Stale-while-revalidate integrado (Netflix grade)
// 🔥 V15: read() mejorado — Detecta datos incompletos y dispara sync
// 🔥 V15: _isDataLikelyIncomplete() — Heurística para detectar dispositivo nuevo
// 🔥 V15: Compatible con InitialSyncEngine sin dependencia directa
// 🔥 V14: CLOUDSTRATEGY VÍNCULO — setLocalStrategy() propagado a CloudStrategy
//    para que auto-repair y orphan cleanup funcionen correctamente
// 🔥 V14: SYNCManager V14 — Phantom Detector + TempID Registry sincronizados
// 🔥 V14: HEALTH CHECK mejorado — reporta autoRepairStats y tempIdRegistry
// 🔥 V14: CREATE/UPDATE con manejo de IDs temporales mejorado
// 🔥 V14: NETWORK RESTORE — flushQueue + reparación de phantoms al reconectar
//
// V13: TOMBSTONE TRIPLE-LAYER (mem + localStorage + IndexedDB)
// V13: DELETE BLINDADO (7 pasos, NUNCA ressuscita)
// V13: BEFORE_PULL_HOOK registrado en SyncManager
// V13: DASHBOARD LOCAL-FIRST (<10ms)
// ======================================================

import IDataStrategy from './IDataStrategy.js';
import LocalStrategy from './LocalStrategy.js';
import CloudStrategy from './CloudStrategy.js';
import SyncManager from '../sync/SyncManager.js';

// ─────────────────────────────────────────────────────
// 🪦 TOMBSTONE KEYS — localStorage (camada de segurança)
// ─────────────────────────────────────────────────────
const LS_TOMBSTONE_KEY  = 'imoveispro_tombstones_v15';
const LS_TOMBSTONE_TTL  = 30 * 24 * 60 * 60 * 1000; // 30 dias

// 🔥 V15: Heurística para detectar datos incompletos
const MIN_EXPECTED_DOCS = 10; // Si hay < 10 docs locales, posible dispositivo nuevo
const CLOUD_CHECK_COOLDOWN = 30_000; // 30s entre verificaciones de integridad

function _lsReadTombstones() {
  try {
    const raw = localStorage.getItem(LS_TOMBSTONE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw);
    const now = Date.now();
    const map = new Map();
    for (const [id, meta] of Object.entries(parsed)) {
      if (meta.deletedAt && (now - meta.deletedAt) < LS_TOMBSTONE_TTL) {
        map.set(String(id), meta);
      }
    }
    return map;
  } catch {
    return new Map();
  }
}

function _lsWriteTombstones(map) {
  try {
    const obj = {};
    for (const [id, meta] of map.entries()) {
      obj[id] = meta;
    }
    localStorage.setItem(LS_TOMBSTONE_KEY, JSON.stringify(obj));
  } catch (e) {
    console.warn('⚠️ [Tombstone-LS] Falha ao persistir:', e.message);
  }
}

function _lsAddTombstone(id, collection, userId) {
  const map = _lsReadTombstones();
  map.set(String(id), { collection, userId, deletedAt: Date.now() });
  _lsWriteTombstones(map);
}

function _lsRemoveTombstone(id) {
  const map = _lsReadTombstones();
  map.delete(String(id));
  _lsWriteTombstones(map);
}

function _lsIsTombstoned(id) {
  return _lsReadTombstones().has(String(id));
}

// ─────────────────────────────────────────────────────
// 🏢 DUAL SYNC STRATEGY V15 — CLASSE PRINCIPAL
// ─────────────────────────────────────────────────────
class DualSyncStrategy extends IDataStrategy {
  constructor(userOrCompanyId, userId = null) {
    super();

    console.log('🔍 DualSyncStrategy V15 constructor:', { userOrCompanyId, userId, tipo: typeof userOrCompanyId });

    let companyId;
    let user;

    // ── Resolver user/companyId (compatibilidade com todos os formatos) ──
    if (userId === null) {
      if (userOrCompanyId && typeof userOrCompanyId === 'object') {
        user       = userOrCompanyId;
        this.userId = user.uid || user.id;
        companyId  = user.companyId || user.company;
        console.log('✅ DualSyncStrategy V15: objeto user detectado (formato novo)');
      } else {
        this.userId = userOrCompanyId;
        console.warn(`⚠️ DualSyncStrategy V15: formato antigo, só userId: ${this.userId}`);
        user      = this._getUserFromStorage(this.userId);
        companyId = this._getCompanyIdFromStorage(this.userId);
      }
    } else {
      companyId   = userOrCompanyId;
      this.userId = userId;
      console.warn(`⚠️ DualSyncStrategy V15: formato antigo, companyId: ${companyId}, userId: ${userId}`);
      user = {
        id: userId, uid: userId,
        companyId, company: companyId,
        canWrite: true, canDelete: true, canRead: true,
        _isAuthenticated: true, _securityLevel: 'user', _tenantId: companyId
      };
    }

    if (!this.userId) throw new Error('DualSyncStrategy V15: userId não definido');

    if (!companyId) {
      companyId = `company_${this.userId}`;
      console.warn(`⚠️ DualSyncStrategy V15: companyId ausente, usando fallback: ${companyId}`);
    }

    if (!user) {
      user = {
        id: this.userId, uid: this.userId,
        companyId, company: companyId,
        canWrite: true, canDelete: true, canRead: true,
        _isAuthenticated: false, _securityLevel: 'fallback', _tenantId: companyId,
        email: `${this.userId}@fallback.com`,
        createdAt: new Date().toISOString()
      };
    }

    this.currentUser      = user;
    this.currentCompanyId = companyId;

    // ── Inicializar estratégias ──────────────────────────────────────────
    try {
      this.local = new LocalStrategy(companyId, this.userId);
      console.log(`📁 LocalStrategy ok: company=${companyId}, user=${this.userId}`);
    } catch (e) {
      console.error('❌ LocalStrategy falhou, usando fallback:', e);
      this.local = this._createFallbackLocalStrategy(companyId, this.userId);
    }

    try {
      this.cloud = new CloudStrategy(user);
      console.log(`☁️ CloudStrategy V12 ok — write=${this.cloud.user?.canWrite}, delete=${this.cloud.user?.canDelete}`);
    } catch (e) {
      console.error('❌ CloudStrategy falhou, usando fallback:', e);
      this.cloud = this._createFallbackCloudStrategy(this.userId);
    }

    // ── SyncManager V14 ──────────────────────────────────────────────────
    const strategyWrapper = { cloud: this.cloud, local: this.local, type: 'dual' };
    this.syncManager = new SyncManager(this.userId, strategyWrapper);

    // 🔥 V14: Vínculos bidireccionales completos
    this._bindAllStrategies();

    // ── Cache de tombstones em memória (camada 1) ──────────────────────
    this._tombstoneMemCache  = new Map();
    this._tombstoneCacheTTL  = 30_000;
    this._lastTombstoneFlush = 0;

    // ── Estado geral ────────────────────────────────────────────────────
    this.mode      = this._detectOptimalMode();
    this.observers = [];

    // 🔥 V15: Control de integridad de datos
    this._lastIntegrityCheck = {};
    this._integrityCheckCooldown = CLOUD_CHECK_COOLDOWN;

    // ── V14: Registrar hook no SyncManager para filtrar PULL ──────────
    this._registerPullHook();

    this._setupNetworkListener();

    console.log(`🚀 DualSyncStrategy V15 pronto — userId: ${this.userId}, company: ${companyId}, modo: ${this.mode}`);
    console.log(`🛡️ V15: readWithSync() | Detección datos incompletos | tombstones 3 camadas | Auto-Repair | Phantom Detector`);
  }

  // ======================================================
  // 🔥 V14: VÍNCULOS BIDIRECIONALES COMPLETOS
  // ======================================================

  _bindAllStrategies() {
    if (!this.syncManager) return;

    if (typeof this.syncManager.setCloudStrategy === 'function') {
      this.syncManager.setCloudStrategy(this.cloud);
      console.log('🔗 [V15] SyncManager ← CloudStrategy vinculado');
    }

    if (typeof this.syncManager.setLocalStrategy === 'function') {
      this.syncManager.setLocalStrategy(this.local);
      console.log('🔗 [V15] SyncManager ← LocalStrategy vinculado');
    } else {
      this.syncManager.localStrategy = this.local;
      console.warn('⚠️ [V15] SyncManager.setLocalStrategy ausente — injetado diretamente');
    }

    const cr = this.syncManager.conflictResolver;
    if (cr) {
      if (typeof cr.setLocalStrategy === 'function') {
        cr.setLocalStrategy(this.local);
        console.log('🔗 [V15] ConflictResolver ← LocalStrategy vinculado');
      } else {
        cr.localStrategy = this.local;
        console.warn('⚠️ [V15] ConflictResolver.setLocalStrategy ausente — injetado diretamente');
      }
    }

    if (this.cloud && typeof this.cloud.setLocalStrategy === 'function') {
      this.cloud.setLocalStrategy(this.local);
      console.log('🔗 [V15] CloudStrategy ← LocalStrategy vinculado (auto-repair habilitado)');
    }
  }

  setLocalStrategy(localStrategy) {
    if (!localStrategy) return;
    this.local = localStrategy;
    this._bindAllStrategies();
    this._invalidateTombstoneCache();
    console.log('🔗 [V15] LocalStrategy atualizado via setLocalStrategy() — todas as estratégias revinculadas');
  }

  _registerPullHook() {
    if (!this.syncManager) return;

    if (typeof this.syncManager.setBeforePullHook === 'function') {
      this.syncManager.setBeforePullHook(async (collection, cloudRecords) => {
        return await this._filterTombstonedRecords(cloudRecords, collection);
      });
      console.log('🔗 [V15] Hook onBeforePull registrado no SyncManager');
    } else {
      const original = this.syncManager.checkForUpdates?.bind(this.syncManager);
      if (original) {
        const self = this;
        this.syncManager.checkForUpdates = async (collection, localData) => {
          const result = await original(collection, localData);
          return result;
        };
        console.log('🔗 [V15] checkForUpdates monkey-patched para filtrar tombstones');
      }
    }
  }

  // ======================================================
  // 🪦 SISTEMA DE TOMBSTONES — TRIPLE CAMADA
  // ======================================================

  async _isTombstonedAnywhere(collection, id) {
    const strId = String(id);

    const memSet = this._tombstoneMemCache.get(collection);
    if (memSet?.has(strId)) return true;

    if (_lsIsTombstoned(strId)) return true;

    try {
      if (this.local && typeof this.local.isTombstoned === 'function') {
        return await this.local.isTombstoned(collection, strId);
      }
    } catch (e) {
      console.warn(`⚠️ [Tombstone] Erro verificando IndexedDB para ${collection}/${strId}:`, e.message);
    }

    return false;
  }

  async _filterTombstonedRecords(records, collection) {
    if (!Array.isArray(records) || records.length === 0) return records;

    try {
      const unified = new Set();

      const memSet = this._tombstoneMemCache.get(collection);
      if (memSet) memSet.forEach(id => unified.add(id));

      const lsMap = _lsReadTombstones();
      for (const [id, meta] of lsMap.entries()) {
        if (!meta.collection || meta.collection === collection) {
          unified.add(id);
        }
      }

      if (this.local && typeof this.local.getTombstonedIds === 'function') {
        try {
          const dbIds = await this.local.getTombstonedIds(collection);
          dbIds.forEach(id => unified.add(String(id)));
        } catch (e) {
          console.warn(`⚠️ [Tombstone] getTombstonedIds falhou para ${collection}:`, e.message);
        }
      }

      if (unified.size === 0) return records;

      const filtered = records.filter(r => {
        const id = String(r?.id || '');
        return !unified.has(id);
      });

      const removed = records.length - filtered.length;
      if (removed > 0) {
        console.log(`🪦 [V15] Filtrados ${removed} tombstoneados de "${collection}"`);
      }

      return filtered;
    } catch (e) {
      console.warn(`⚠️ [Tombstone] _filterTombstonedRecords falhou para ${collection}:`, e.message);
      return records;
    }
  }

  _invalidateTombstoneCache(collection = null) {
    if (collection) {
      this._tombstoneMemCache.delete(collection);
    } else {
      this._tombstoneMemCache.clear();
    }
    this._lastTombstoneFlush = 0;
  }

  // ======================================================
  // 🗑️ DELETE — BLINDADO V15
  // ======================================================

  async delete(collection, id, options = {}) {
    const strId = String(id);
    console.log(`🗑️ [V15] delete: ${collection}/${strId}`);
    const t0 = performance.now();

    _lsAddTombstone(strId, collection, this.userId);

    if (!this._tombstoneMemCache.has(collection)) {
      this._tombstoneMemCache.set(collection, new Set());
    }
    this._tombstoneMemCache.get(collection).add(strId);

    try {
      if (this.local && typeof this.local.createTombstone === 'function') {
        await this.local.createTombstone(collection, strId, {
          userId: this.userId,
          deletedBy: this.userId,
          reason: options.reason || 'user_delete',
          _version: options._version || 1
        });
        console.log(`🪦 [V15] Tombstone IndexedDB criado: ${collection}/${strId}`);
      }

      const localResult = await this.local.delete(collection, strId, options);
      console.log(`✅ [V15] IndexedDB delete ok: ${collection}/${strId}`);

      this._invalidateTombstoneCache(collection);

      if (this.syncManager && typeof this.syncManager.notifyDelete === 'function') {
        this.syncManager.notifyDelete(collection, strId);
      }

      const canDeleteCloud = this.cloud?.user?.canDelete !== false;

      if ((this.mode === 'dual' || this.mode === 'cloud') && navigator.onLine && this.cloud && canDeleteCloud) {
        const cloudOk = await this._propagateDeleteToCloud(collection, strId, options);

        if (cloudOk) {
          _lsRemoveTombstone(strId);
          console.log(`🧹 [V15] Tombstone localStorage removido após ACK cloud: ${strId}`);
        } else {
          console.log(`📋 [V15] Tombstone localStorage mantido (cloud ainda não confirmou): ${strId}`);
        }
      } else if (!navigator.onLine && this.syncManager) {
        console.log(`📴 [V15] Offline — delete encolado no SyncManager: ${collection}/${strId}`);
        await this.syncManager.delete(collection, strId);
      }

      this._notifyObservers({
        type: 'DELETE', collection, id: strId,
        tombstone: true,
        duration: performance.now() - t0
      });

      return {
        ...localResult,
        tombstone: true,
        _deletedAt: new Date().toISOString(),
        _deletedBy: this.userId
      };

    } catch (error) {
      console.error(`❌ [V15] delete falhou: ${collection}/${strId}`, error);
      throw error;
    }
  }

  async _propagateDeleteToCloud(collection, id, options = {}) {
    const maxRetries = options.maxRetries || 3;
    let lastError = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        if (typeof this.cloud.delete === 'function') {
          await this.cloud.delete(collection, id);
        } else if (typeof this.cloud.save === 'function') {
          await this.cloud.save(collection, {
            _deleted: true,
            _deletedAt: new Date().toISOString(),
            _deletedBy: this.userId
          }, id);
        }
        console.log(`☁️ [V15] Cloud delete ok (tentativa ${attempt + 1}): ${collection}/${id}`);
        return true;
      } catch (e) {
        lastError = e;
        const isPermissionError =
          e.code === 'permission-denied' ||
          e.message?.includes('Missing or insufficient permissions') ||
          e.message?.includes('permisos');
        if (isPermissionError) {
          console.log(`🔒 [V15] Sem permissão para delete no cloud: ${collection}/${id}`);
          return false;
        }
        if (attempt < maxRetries - 1) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
          console.warn(`⚠️ [V15] Cloud delete falhou (${attempt + 1}/${maxRetries}), retry em ${delay}ms:`, e.message);
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }

    console.error(`❌ [V15] Cloud delete falhou após ${maxRetries} tentativas: ${collection}/${id}`, lastError?.message);
    if (this.syncManager) {
      await this.syncManager.delete(collection, id);
      console.log(`📋 [V15] Delete encolado no SyncManager: ${collection}/${id}`);
    }
    return false;
  }

  // ======================================================
  // 🔥 V15: DETECCIÓN DE DATOS INCOMPLETOS
  // ======================================================

  /**
   * Heurística para detectar si los datos locales están probablemente incompletos.
   * 
   * Indicadores:
   * 1. Pocos documentos (< MIN_EXPECTED_DOCS)
   * 2. No hay datos antiguos (todo creado recientemente)
   * 3. El dispositivo nunca hizo un sync completo
   * 
   * @returns {boolean} true si probablemente es un dispositivo nuevo
   */
  _isDataLikelyIncomplete(collection, localData) {
    if (!Array.isArray(localData)) return true;
    
    // Si hay 0 documentos, definitivamente está incompleto
    if (localData.length === 0) return true;
    
    // Si hay pocos documentos, posible dispositivo nuevo
    if (localData.length < MIN_EXPECTED_DOCS) return true;
    
    // Verificar si hay datos con antigüedad significativa (> 1 hora)
    // Si todos los documentos son muy recientes, posible sync parcial
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    const hasOldData = localData.some(item => {
      const createdAt = new Date(item.createdAt || item._syncedAt || 0).getTime();
      const syncedAt = new Date(item._syncedAt || 0).getTime();
      const maxTime = Math.max(createdAt, syncedAt);
      return maxTime > 0 && maxTime < oneHourAgo;
    });
    
    // Si no hay datos viejos y hay cloud disponible, podría ser incompleto
    if (!hasOldData && this.cloud && navigator.onLine) {
      return true;
    }
    
    return false;
  }

  // ======================================================
  // 🔥 V15: READ CON STALE-WHILE-REVALIDATE
  // ======================================================

  /**
   * V15: Método de lectura con estrategia Netflix-grade:
   * 1. Retorna datos locales INMEDIATAMENTE
   * 2. Si detecta datos incompletos, dispara sync en background
   * 3. La UI recibe datos al instante; el sync actualiza después
   * 
   * @param {string} collection - Nombre de la colección
   * @param {Object} query - Filtros de búsqueda
   * @param {Object} options - Opciones adicionales
   * @param {boolean} options.awaitSync - Si true, espera el sync antes de retornar
   * @returns {Array} Datos de la colección
   */
  async readWithSync(collection, query = {}, options = {}) {
    const t0 = performance.now();
    
    try {
      // Paso 1: Leer datos locales inmediatamente
      let localData = await this.local.read(collection, query, options);
      localData = Array.isArray(localData) ? localData : [];
      
      // Filtrar tombstones
      localData = await this._filterTombstonedRecords(localData, collection);
      
      // Paso 2: Verificar si los datos parecen incompletos
      const likelyIncomplete = this._isDataLikelyIncomplete(collection, localData);
      const shouldCheckCloud = navigator.onLine && this.cloud && this.syncManager;
      
      // Control de cooldown para no saturar
      const now = Date.now();
      const lastCheck = this._lastIntegrityCheck[collection] || 0;
      const cooldownPassed = (now - lastCheck) > this._integrityCheckCooldown;
      
      if (likelyIncomplete && shouldCheckCloud && cooldownPassed) {
        this._lastIntegrityCheck[collection] = now;
        
        console.log(`🔍 [V15] Datos posiblemente incompletos en "${collection}" (${localData.length} docs) — verificando cloud...`);
        
        if (options.awaitSync) {
          // Modo espera: sincronizar antes de retornar (útil para primera carga)
          console.log(`⏳ [V15] readWithSync: esperando sync de "${collection}"...`);
          
          try {
            // Disparar sync y esperar
            await this.syncManager.syncFromCloud(collection, localData);
            
            // Releer datos locales después del sync
            localData = await this.local.read(collection, query, options);
            localData = Array.isArray(localData) ? localData : [];
            localData = await this._filterTombstonedRecords(localData, collection);
            
            console.log(`✅ [V15] readWithSync completado: ${localData.length} docs en ${collection}`);
          } catch (syncError) {
            console.warn(`⚠️ [V15] Sync falló en readWithSync, retornando datos locales:`, syncError.message);
          }
        } else {
          // Modo background: disparar sync sin bloquear
          console.log(`🔄 [V15] Disparando sync background para "${collection}"...`);
          this.syncManager.checkForUpdates(collection, localData);
        }
      } else if (shouldCheckCloud && !cooldownPassed) {
        // Sync normal en background (cooldown)
        this.syncManager.checkForUpdates(collection, localData);
      }
      
      const elapsed = performance.now() - t0;
      if (elapsed > 50) {
        console.log(`⏱️ [V15] readWithSync("${collection}") — ${localData.length} docs en ${Math.round(elapsed)}ms`);
      }
      
      return localData;
      
    } catch (error) {
      console.error(`❌ [V15] readWithSync ${collection} falhou:`, error);
      
      // Fallback: retornar datos locales sin filtro
      try {
        const fallbackData = await this.local.read(collection, query, {});
        return Array.isArray(fallbackData) ? fallbackData : [];
      } catch {
        return [];
      }
    }
  }

  // ======================================================
  // 📖 READ — V15 MEJORADO CON DETECCIÓN DE INCOMPLETOS
  // ======================================================

  async read(collection, query = {}, options = {}) {
    const strategy = options.strategy || this.mode;

    try {
      let data;

      switch (strategy) {
        case 'local':
          data = await this.local.read(collection, query, options);
          break;

        case 'cloud':
          data = await this.cloud.read(collection, query, options);
          break;

        case 'dual':
        default:
          // LOCAL-FIRST: resposta imediata do IndexedDB
          data = await this.local.read(collection, query, options);

          // 🔥 V15: Verificar si los datos están incompletos
          const likelyIncomplete = this._isDataLikelyIncomplete(collection, data);
          const now = Date.now();
          const lastCheck = this._lastIntegrityCheck[collection] || 0;
          const cooldownPassed = (now - lastCheck) > this._integrityCheckCooldown;
          
          if (navigator.onLine && this.syncManager && this.cloud) {
            if (likelyIncomplete && cooldownPassed) {
              // Datos posiblemente incompletos — sync más agresivo
              this._lastIntegrityCheck[collection] = now;
              console.log(`🔍 [V15] read("${collection}"): ${data.length} docs locales parecen incompletos — sync prioritario`);
              this.syncManager.syncFromCloud(collection, data).catch(e => 
                console.warn(`⚠️ [V15] syncFromCloud falló en read:`, e.message)
              );
            } else if (!this._lastCheck) {
              this._lastCheck = {};
            }
            
            // Background sync normal con cooldown de 10s
            if (!this._lastCheck[collection] || now - this._lastCheck[collection] > 10_000) {
              this._lastCheck[collection] = now;
              this.syncManager.checkForUpdates(collection, data);
            }
          }
          break;
      }

      // V15: Filtrar tombstoneados em TODA leitura
      if (Array.isArray(data)) {
        data = await this._filterTombstonedRecords(data, collection);
      }

      return data;

    } catch (error) {
      console.error(`❌ [V15] read ${collection} falhou:`, error);
      return [];
    }
  }

  // ======================================================
  // ✍️ CREATE — V15 IDEMPOTENTE (SINGLE WRITER)
  // ======================================================

  async create(collection, data, options = {}) {
    const t0 = performance.now();

    try {
      const localResult = await this.local.create(collection, data, options);
      const localId = localResult?.id || data?.id || data?._localId;

      const result = {
        ...localResult,
        id: localId,
        userId: this.userId,
        companyId: this.currentCompanyId,
        source: 'dual_local',
        syncStatus: 'pending'
      };

      if ((this.mode === 'dual' || this.mode === 'cloud') && navigator.onLine && this.cloud) {
        try {
          let cloudResult;
          const dataWithId = { ...data, id: localId };
          
          if (typeof this.cloud.save === 'function') {
            cloudResult = await this.cloud.save(collection, dataWithId);
          } else if (typeof this.cloud.create === 'function') {
            cloudResult = await this.cloud.create(collection, dataWithId);
          }

          if (cloudResult) {
            const cloudId = cloudResult?.id || cloudResult?.docId || cloudResult?._id;
            result.syncStatus = 'synced';
            result.cloudId = cloudId || localId;

            if (cloudId && String(cloudId) !== String(localId)) {
              console.log(`🔄 [V15] ID sincronizado: ${localId} → ${cloudId}`);
              
              try {
                await this.local.delete(collection, localId, { silent: true }).catch(() => {});
                const mergedData = {
                  ...data,
                  ...cloudResult,
                  id: cloudId,
                  _cloudId: cloudId,
                  _syncedAt: new Date().toISOString()
                };
                await this.local.create(collection, mergedData).catch(() => {
                  return this.local.update(collection, localId, {
                    ...data,
                    id: cloudId,
                    _cloudId: cloudId,
                    _syncedAt: new Date().toISOString()
                  });
                });
                result.id = cloudId;
              } catch (e) {
                console.warn(`⚠️ [V15] Não foi possível sincronizar ID no IndexedDB:`, e.message);
              }
              
              if (this.syncManager && typeof this.syncManager.registerIdMapping === 'function') {
                this.syncManager.registerIdMapping(localId, cloudId);
              }
            } else if (cloudId && String(cloudId) === String(localId)) {
              await this.local.update(collection, localId, {
                _syncStatus: 'synced',
                _syncedAt: new Date().toISOString(),
                _cloudId: cloudId
              }).catch(() => {});
            }
          }
        } catch (cloudError) {
          console.warn(`⚠️ [V15] Cloud create falhou, encolando:`, cloudError.message);
          if (this.syncManager) {
            await this.syncManager.create(collection, { ...data, id: localId }).catch(() => {});
          }
        }
      } else if (!navigator.onLine && this.syncManager) {
        await this.syncManager.create(collection, { ...data, id: localId }).catch(() => {});
      }

      this._notifyObservers({ type: 'CREATE', collection, data: result, duration: performance.now() - t0 });
      return result;

    } catch (error) {
      console.error(`❌ [V15] create ${collection}:`, error);
      throw error;
    }
  }

  // ======================================================
  // ✏️ UPDATE — V14 COM AUTO-REPAIR DELEGADO
  // ======================================================

  async update(collection, id, data, options = {}) {
    console.log(`🔄 [V15] update: ${collection}/${id}`);

    try {
      const tombstoned = await this._isTombstonedAnywhere(collection, id);
      if (tombstoned) {
        console.warn(`⚠️ [V15] Update bloqueado — registro tombstoneado: ${collection}/${id}`);
        return {
          id,
          _warning: 'Record is tombstoned — update ignored',
          _tombstoned: true,
          updatedAt: new Date().toISOString()
        };
      }

      const localResult = await this.local.update(collection, id, data, options);

      const result = {
        ...localResult,
        userId: this.userId,
        companyId: this.currentCompanyId,
        source: 'dual_local',
        syncStatus: 'pending',
        updatedAt: new Date().toISOString()
      };

      if ((this.mode === 'dual' || this.mode === 'cloud') && navigator.onLine && this.cloud) {
        try {
          if (typeof this.cloud.save === 'function') {
            await this.cloud.save(collection, data, id);
          } else if (typeof this.cloud.update === 'function') {
            const cloudResult = await this.cloud.update(collection, id, data);

            if (cloudResult?.id && String(cloudResult.id) !== String(id)) {
              await this.local.update(collection, id, {
                ...data, id: cloudResult.id,
                _cloudId: cloudResult.id,
                _syncedAt: new Date().toISOString()
              }).catch(() => {});
              result.id = cloudResult.id;

              if (this.syncManager && typeof this.syncManager.registerIdMapping === 'function') {
                this.syncManager.registerIdMapping(id, cloudResult.id);
              }
              console.log(`🔄 [V15] ID reparado por CloudStrategy: ${id} → ${cloudResult.id}`);
            }
          }
          result.syncStatus = 'synced';
        } catch (cloudError) {
          console.warn(`⚠️ [V15] Cloud update falhou, encolando:`, cloudError.message);
          if (this.syncManager) await this.syncManager.update(collection, id, data);
        }
      } else if (this.syncManager) {
        await this.syncManager.update(collection, id, data);
      }

      return result;

    } catch (error) {
      console.error(`❌ [V15] update ${collection}/${id}:`, error);
      throw error;
    }
  }

  // ======================================================
  // 💾 SAVE (upsert)
  // ======================================================

  async save(collection, data, id = null) {
    if (id) return await this.update(collection, id, data);
    return await this.create(collection, data);
  }

  // ======================================================
  // 🔍 TOMBSTONE — API PÚBLICA
  // ======================================================

  async isTombstoned(collection, id) {
    return await this._isTombstonedAnywhere(collection, id);
  }

  async getActiveTombstones(collection = null) {
    try {
      if (this.local && typeof this.local.getActiveTombstones === 'function') {
        return await this.local.getActiveTombstones(collection);
      }
    } catch (e) {
      console.warn('⚠️ [V15] getActiveTombstones falhou:', e.message);
    }
    return [];
  }

  // ======================================================
  // 📊 DASHBOARD — LOCAL-FIRST
  // ======================================================

  async getDashboardStats(userId) {
    const uid = userId || this.userId;
    console.log(`📊 [V15] getDashboardStats LOCAL-FIRST para: ${uid}`);

    if (this.local && typeof this.local.getDashboardStats === 'function') {
      try {
        const stats = await this.local.getDashboardStats(uid);
        return { ...stats, source: 'local_strategy_v15' };
      } catch (e) {
        console.warn('⚠️ LocalStrategy.getDashboardStats falhou:', e.message);
      }
    }

    if (this.local?.instance && typeof this.local.instance.getDashboardStats === 'function') {
      try {
        const stats = await this.local.instance.getDashboardStats();
        return { ...stats, source: 'database_js' };
      } catch (e) {
        console.warn('⚠️ database.js getDashboardStats falhou:', e.message);
      }
    }

    return await this._calculateDashboardStats();
  }

  async _calculateDashboardStats() {
    try {
      let [properties, clients, tasks, financeiro] = await Promise.all([
        this.local.read('properties', {}, {}).catch(() => []),
        this.local.read('clients',    {}, {}).catch(() => []),
        this.local.read('tasks',      {}, {}).catch(() => []),
        this.local.read('financeiro', {}, {}).catch(() => [])
      ]);

      [properties, clients, tasks] = await Promise.all([
        this._filterTombstonedRecords(properties, 'properties'),
        this._filterTombstonedRecords(clients,    'clients'),
        this._filterTombstonedRecords(tasks,      'tasks')
      ]);

      const now     = new Date();
      const month   = now.getMonth();
      const year    = now.getFullYear();

      const activeProperties = properties.filter(p =>
        ['Disponível', 'available', 'active'].includes(p.status)
      ).length;

      const activeClients = clients.filter(c =>
        ['Ativo', 'active'].includes(c.status)
      ).length;

      const pendingTasks = tasks.filter(t =>
        ['pending', 'pendente', 'in_progress'].includes(t.status)
      ).length;

      const monthlyRevenue = financeiro
        .filter(f => {
          if (f.type !== 'receita') return false;
          const d = new Date(f.date || f.createdAt);
          return d.getMonth() === month && d.getFullYear() === year;
        })
        .reduce((sum, f) => sum + (Number(f.value) || 0), 0);

      const linked = clients.filter(
        c => c.propertyId || c.propertyIds?.length > 0
      ).length;
      const conversionRate = clients.length > 0
        ? Math.round((linked / clients.length) * 100)
        : 0;

      return {
        activeProperties, activeClients, pendingTasks,
        monthlyRevenue,   conversionRate,
        totalProperties: properties.length,
        totalClients:    clients.length,
        totalTasks:      tasks.length,
        source:    'manual_calculation_v15',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ [V15] _calculateDashboardStats falhou:', error);
      return {
        activeProperties: 0, activeClients: 0, pendingTasks: 0,
        monthlyRevenue: 0,   conversionRate: 0,
        totalProperties: 0,  totalClients: 0, totalTasks: 0,
        source: 'fallback_error', error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  // ======================================================
  // 📅 MÉTODOS DE RELATÓRIO — LOCAL-FIRST
  // ======================================================

  async getTasksByClient(clientId, userId) {
    try {
      let tasks = await this.local.read('tasks', {}, {}) || [];
      tasks = await this._filterTombstonedRecords(tasks, 'tasks');
      return tasks.filter(t => t.clientId === clientId);
    } catch (e) {
      console.warn('⚠️ [V15] getTasksByClient local falhou:', e.message);
    }
    try {
      if (this.cloud && typeof this.cloud.getTasksByClient === 'function') {
        return await this.cloud.getTasksByClient(clientId, userId || this.userId);
      }
    } catch (e) {
      console.warn('⚠️ [V15] getTasksByClient cloud falhou:', e.message);
    }
    return [];
  }

  async getCalendarEventsByProperty(propertyId, userId) {
    try {
      let events = await this.local.read('calendarEvents', {}, {}) || [];
      events = await this._filterTombstonedRecords(events, 'calendarEvents');
      return events.filter(e => e.propertyId === propertyId);
    } catch (e) {
      console.warn('⚠️ [V15] getCalendarEventsByProperty local falhou:', e.message);
    }
    try {
      if (this.cloud && typeof this.cloud.getCalendarEventsByProperty === 'function') {
        return await this.cloud.getCalendarEventsByProperty(propertyId, userId || this.userId);
      }
    } catch (e) {
      console.warn('⚠️ [V15] getCalendarEventsByProperty cloud falhou:', e.message);
    }
    return [];
  }

  async getMonthlySales(months = 6) {
    try {
      if (this.local?.instance?.getMonthlySales) {
        return await this.local.instance.getMonthlySales(months);
      }
    } catch (e) {}

    try {
      const financeiro = await this.local.read('financeiro', {}, {}) || [];
      const today = new Date();
      return Array.from({ length: months }, (_, i) => {
        const date = new Date(today.getFullYear(), today.getMonth() - (months - 1 - i), 1);
        const total = financeiro
          .filter(f => {
            if (f.type !== 'receita' || f.category !== 'Venda de Imóvel') return false;
            const d = new Date(f.date || f.createdAt);
            return d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
          })
          .reduce((s, f) => s + (Number(f.value) || 0), 0);
        return {
          month: date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }),
          value: total, fullDate: date
        };
      });
    } catch (e) {
      console.warn('⚠️ [V15] getMonthlySales falhou:', e.message);
      return [];
    }
  }

  async getMonthlyRentals(months = 6) {
    try {
      if (this.local?.instance?.getMonthlyRentals) {
        return await this.local.instance.getMonthlyRentals(months);
      }
    } catch (e) {}

    try {
      const financeiro = await this.local.read('financeiro', {}, {}) || [];
      const today = new Date();
      return Array.from({ length: months }, (_, i) => {
        const date = new Date(today.getFullYear(), today.getMonth() - (months - 1 - i), 1);
        const items = financeiro.filter(f => {
          if (f.category !== 'Aluguel') return false;
          const d = new Date(f.date || f.createdAt);
          return d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
        });
        return {
          month: date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }),
          monthlyValue: items.reduce((s, f) => s + (Number(f.value) || 0), 0),
          count: items.length, fullDate: date
        };
      });
    } catch (e) {
      console.warn('⚠️ [V15] getMonthlyRentals falhou:', e.message);
      return [];
    }
  }

  async getSalesGrowth() {
    try {
      if (this.local?.instance?.getSalesGrowth) {
        return await this.local.instance.getSalesGrowth();
      }
    } catch (e) {}

    try {
      const monthly = await this.getMonthlySales(2);
      if (monthly.length < 2) return { percentage: 0, trend: 'stable', formatted: '0%' };
      const prev = monthly[0]?.value || 0;
      const curr = monthly[1]?.value || 0;
      if (prev === 0) return {
        percentage: curr > 0 ? 100 : 0,
        trend: curr > 0 ? 'up' : 'stable',
        formatted: curr > 0 ? '+100%' : '0%',
        source: 'manual_v15'
      };
      const growth  = ((curr - prev) / prev) * 100;
      const rounded = Math.round(growth * 10) / 10;
      return {
        percentage: rounded,
        trend: rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'stable',
        formatted: `${rounded > 0 ? '+' : ''}${rounded}%`,
        source: 'manual_v15'
      };
    } catch (e) {
      return { percentage: 0, trend: 'stable', formatted: '0%', source: 'fallback_error' };
    }
  }

  // ======================================================
  // 🔧 UTILITÁRIOS INTERNOS
  // ======================================================

  _getUserFromStorage(userId) {
    try {
      const raw = localStorage.getItem('auth_user');
      if (raw) {
        const u = JSON.parse(raw);
        if (u.id === userId) {
          return {
            ...u, uid: u.uid || u.id,
            companyId: u.companyId || u.company,
            canWrite:  u.canWrite  ?? true,
            canDelete: u.canDelete ?? (u.role === 'admin'),
            canRead:   u.canRead   ?? true,
            _isAuthenticated: true,
            _securityLevel:   u.role || 'user',
            _tenantId:        u.companyId || u.company
          };
        }
      }
    } catch (e) {
      console.warn('⚠️ _getUserFromStorage:', e);
    }
    return null;
  }

  _getCompanyIdFromStorage(userId) {
    try {
      const raw = localStorage.getItem('auth_user');
      if (raw) {
        const u = JSON.parse(raw);
        if (u.id === userId) return u.companyId || u.company;
      }
      const cc = localStorage.getItem('current_company');
      if (cc) return cc;
    } catch (e) {
      console.warn('⚠️ _getCompanyIdFromStorage:', e);
    }
    return `company_${userId}`;
  }

  _createFallbackLocalStrategy(companyId, userId) {
    return {
      create:               async (c, d)    => ({ id: 'fallback_' + Date.now(), ...d, userId, companyId, source: 'fallback_local', createdAt: new Date().toISOString() }),
      read:                 async ()        => [],
      update:               async (c, id, d) => ({ id, ...d, userId, companyId, updatedAt: new Date().toISOString() }),
      delete:               async ()        => ({ success: true }),
      save:                 async ()        => ({ success: true, id: 'fallback_' + Date.now() }),
      healthCheck:          async ()        => ({ status: 'fallback', type: 'local_fallback' }),
      createTombstone:      async ()        => null,
      isTombstoned:         async ()        => false,
      getActiveTombstones:  async ()        => [],
      getTombstonedIds:     async ()        => new Set(),
      removeTombstone:      async ()        => false,
      bulkPut:              async ()        => null
    };
  }

  _createFallbackCloudStrategy(userId) {
    return {
      create:      async (c, d)      => ({ id: 'fallback_cloud_' + Date.now(), ...d, userId, source: 'fallback_cloud' }),
      read:        async ()          => [],
      update:      async (c, id, d)  => ({ id, ...d, userId }),
      delete:      async ()          => ({ success: true }),
      save:        async ()          => ({ success: true, id: 'fallback_cloud_' + Date.now() }),
      healthCheck: async ()          => ({ status: 'fallback', type: 'cloud_fallback' }),
      setLocalStrategy: async ()     => {}
    };
  }

  _detectOptimalMode() {
    return navigator.onLine ? 'dual' : 'local';
  }

  // ======================================================
  // 🌐 NETWORK LISTENERS — V15 MEJORADO
  // ======================================================

  _setupNetworkListener() {
    window.addEventListener('online', () => {
      this.mode = 'dual';
      console.log('🌐 [V15] Online — procesando cola y sincronizando...');

      if (this.syncManager) {
        this.syncManager.flushQueue?.();
      }

      // Resetear cooldowns para forzar verificación de integridad
      this._lastIntegrityCheck = {};

      if (this.syncManager && this._lastCheck) {
        Object.keys(this._lastCheck).forEach(collection => {
          this.syncManager.checkForUpdates(collection, []);
        });
      }

      this._notifyObservers({ type: 'NETWORK_RESTORED' });
    });

    window.addEventListener('offline', () => {
      this.mode = 'local';
      console.log('📴 [V15] Offline — modo local ativado');
      this._notifyObservers({ type: 'NETWORK_LOST' });
    });
  }

  // ======================================================
  // 📡 OBSERVERS
  // ======================================================

  addObserver(observer) {
    if (typeof observer === 'function') this.observers.push(observer);
  }

  _notifyObservers(event) {
    this.observers.forEach(o => {
      try { if (typeof o === 'function') o(event); } catch (e) {}
    });
  }

  notifyObservers(event) { this._notifyObservers(event); }

  // ======================================================
  // 🏥 HEALTH CHECK — V15 MEJORADO
  // ======================================================

  async healthCheck() {
    try {
      const [localHealth, cloudHealth] = await Promise.all([
        this.local.healthCheck().catch(e => ({ status: 'error', error: e.message })),
        this.cloud.healthCheck().catch(e => ({ status: 'error', error: e.message }))
      ]);

      const syncStatus = this.syncManager?.getStatus?.() || {};
      const lsTombstones = _lsReadTombstones().size;

      return {
        version: 'V15',
        mode: this.mode,
        online: navigator.onLine,
        local: localHealth,
        cloud: cloudHealth,
        sync: {
          ...syncStatus,
          queueSize: syncStatus.queueSize || 0,
          phantomDetector: syncStatus.phantomDetector || false,
          autoRepair: syncStatus.autoRepair || false,
          autoRepairStats: syncStatus.autoRepairStats || { total: 0, byCollection: {} },
          tempIdRegistrySize: syncStatus.tempIdRegistrySize || 0
        },
        tombstones: {
          memCache:      [...this._tombstoneMemCache.values()].reduce((s, v) => s + v.size, 0),
          localStorage:  lsTombstones,
          cloudLinked:   !!this.cloud?._localStrategy,
          syncManagerLinked: !!this.syncManager?.localStrategy
        },
        bindings: {
          cloudToLocal: !!this.cloud?._localStrategy,
          syncManagerToCloud: !!this.syncManager?.cloudStrategy,
          syncManagerToLocal: !!this.syncManager?.localStrategy,
          conflictResolverToLocal: !!this.syncManager?.conflictResolver?.localStrategy
        },
        features: {
          readWithSync: true,
          incompleteDataDetection: true,
          staleWhileRevalidate: true,
          integrityCooldown: this._integrityCheckCooldown,
        },
        userId:    this.userId,
        companyId: this.currentCompanyId,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        version: 'V15', mode: this.mode, online: navigator.onLine,
        error: error.message, userId: this.userId, companyId: this.currentCompanyId,
        timestamp: new Date().toISOString()
      };
    }
  }

  // ======================================================
  // 🔧 API PÚBLICA DE CONTROLE
  // ======================================================

  getUser()           { return this.currentUser; }
  updateUser(u)       { if (u) this.currentUser = { ...this.currentUser, ...u }; }
  forceCloudMode()    { this.mode = 'cloud'; }
  forceLocalMode()    { this.mode = 'local'; }

  async cleanDuplicatedUsers() {
    if (this.cloud?.cleanDuplicatedUsers) return await this.cloud.cleanDuplicatedUsers();
    throw new Error('cleanDuplicatedUsers não disponível');
  }
}

export default DualSyncStrategy;